import { useEffect, useMemo, useRef, useState } from "react";
import { FlatList, Platform, Pressable, StyleSheet, View } from "react-native";
import { useCustomTheme } from "../hooks/useCustomTheme";
import { Icon } from "./Icon";
import { TextInput } from "./TextInput";
import { ThemedDropdown } from "./ThemedDropdown";
import { ThemedText } from "./ThemedText";
export function Select({ options, placeholder, disabled = false, multiple = false, value = multiple ? [] : null, onChange, clearable = true, maxSelected, error, icon, numColumns, dropdownFooter, reserveErrorSpace, testID, }) {
    const { colors } = useCustomTheme();
    const [open, setOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [focusedOption, setFocusedOption] = useState(null);
    const anchorRef = useRef(null);
    const initialSelected = useMemo(() => {
        if (multiple)
            return (Array.isArray(value) ? value : []);
        return (value === null || Array.isArray(value) ? null : value);
    }, [value, multiple]);
    const [tempSelected, setTempSelected] = useState(multiple ? initialSelected : initialSelected);
    useEffect(() => {
        setTempSelected((prev) => {
            const next = multiple
                ? initialSelected
                : initialSelected;
            if (multiple) {
                const prevArr = (Array.isArray(prev) ? prev : []);
                const nextArr = (Array.isArray(next) ? next : []);
                if (prevArr.length === nextArr.length) {
                    let allEqual = true;
                    for (let i = 0; i < prevArr.length; i += 1) {
                        if (prevArr[i] !== nextArr[i]) {
                            allEqual = false;
                            break;
                        }
                    }
                    if (allEqual) {
                        return prev;
                    }
                }
                return nextArr;
            }
            if (prev === next) {
                return prev;
            }
            return next;
        });
    }, [initialSelected, multiple]);
    useEffect(() => {
        if (!open) {
            setFocusedOption(null);
        }
    }, [open]);
    const shouldKeepDropdownOpenForSelection = useMemo(() => {
        if (multiple) {
            return false;
        }
        const selectedValue = initialSelected;
        if (selectedValue == null) {
            return false;
        }
        return (options.find((option) => option.value === selectedValue)
            ?.keepOpenOnSelect === true);
    }, [initialSelected, multiple, options]);
    const previousInitialSelectedRef = useRef(initialSelected);
    useEffect(() => {
        const previousInitialSelected = previousInitialSelectedRef.current;
        previousInitialSelectedRef.current = initialSelected;
        if (!open || multiple) {
            return;
        }
        if (previousInitialSelected === initialSelected) {
            return;
        }
        if (initialSelected == null || shouldKeepDropdownOpenForSelection) {
            return;
        }
        setOpen(false);
    }, [initialSelected, multiple, open, shouldKeepDropdownOpenForSelection]);
    const [hoveredOption, setHoveredOption] = useState(null);
    const triggerStyle = Platform.OS === "web"
        ? [
            disabled ? styles.disabled : undefined,
            { outlineStyle: "none" },
        ]
        : disabled
            ? styles.disabled
            : undefined;
    const displayText = useMemo(() => {
        if (multiple) {
            const vals = tempSelected ?? [];
            if (vals.length === 0)
                return placeholder;
            return vals
                .map((v) => options.find((o) => o.value === v)?.label ?? String(v))
                .join(", ");
        }
        const v = tempSelected;
        if (v == null)
            return placeholder;
        return options.find((o) => o.value === v)?.label ?? String(v);
    }, [tempSelected, multiple, options, placeholder]);
    const isSelected = (v) => {
        if (multiple)
            return tempSelected.includes(v);
        return tempSelected === v;
    };
    const toggleValue = (option) => {
        const { value: v, keepOpenOnSelect } = option;
        if (multiple) {
            const arr = new Set(tempSelected);
            if (arr.has(v)) {
                arr.delete(v);
            }
            else {
                if (maxSelected !== undefined && arr.size >= maxSelected)
                    return;
                arr.add(v);
            }
            setTempSelected(Array.from(arr));
            onChange(Array.from(arr));
        }
        else {
            setTempSelected(v);
            if (!keepOpenOnSelect) {
                setOpen(false);
            }
            onChange(v);
        }
    };
    const openDropdown = () => {
        if (disabled)
            return;
        setOpen(true);
    };
    const clearSelection = () => {
        if (disabled)
            return;
        if (multiple) {
            setTempSelected([]);
            onChange([]);
        }
        else {
            setTempSelected(null);
            onChange(null);
        }
    };
    const runOnSpaceOrEnter = (event, action) => {
        const key = event.nativeEvent?.key;
        if (key !== " " && key !== "Enter") {
            return;
        }
        event.preventDefault?.();
        action();
    };
    const renderOption = ({ item }) => {
        const optionKey = String(item.value);
        const selected = isSelected(item.value);
        const disabledItem = !!item.disabled;
        const optionFocused = focusedOption === optionKey;
        const optionHovered = hoveredOption === optionKey;
        const optionKeyboardProps = {
            onKeyDown: (event) => {
                if (disabledItem)
                    return;
                runOnSpaceOrEnter(event, () => toggleValue(item));
            },
        };
        return (<Pressable style={[
                styles.option,
                (disabled || disabledItem) && styles.disabled,
                Platform.OS === "web" ? { outlineStyle: "none" } : undefined,
                {
                    backgroundColor: optionHovered || optionFocused
                        ? colors.sectionBackground
                        : colors.background,
                },
                optionFocused && styles.focusedBorder,
                optionFocused && {
                    borderColor: colors.palette.spinach30,
                },
            ]} onPress={() => !disabledItem && toggleValue(item)} onFocus={() => {
                setFocusedOption(optionKey);
            }} onBlur={() => {
                setFocusedOption((current) => current === optionKey ? null : current);
            }} onHoverIn={() => {
                setHoveredOption(optionKey);
            }} onHoverOut={() => {
                setHoveredOption(null);
            }} {...optionKeyboardProps}>
        {multiple ? (<Icon iconSet="material-community" icon={selected ? "checkbox-marked" : "checkbox-blank-outline"} size={24} color={colors.border}/>) : null}
        {item.renderingLabel?.() ?? (<ThemedText style={styles.optionLabel}>{item.label}</ThemedText>)}
      </Pressable>);
    };
    const currentHasValue = multiple
        ? (tempSelected?.length ?? 0) > 0
        : tempSelected != null;
    const slotRight = (<>
      {clearable && currentHasValue && (<Pressable style={styles.clear} onPress={(e) => {
                e.stopPropagation?.();
                clearSelection();
            }}>
          <Icon iconSet="material-community" icon="close" size={24} color={colors.text}/>
        </Pressable>)}
      <Icon iconSet="material-community" icon="menu-down" size={28} color={colors.text}/>
    </>);
    const triggerKeyboardProps = {
        onKeyDown: (event) => {
            runOnSpaceOrEnter(event, openDropdown);
        },
    };
    return (<View style={styles.formGroup}>
      <View>
        <Pressable ref={anchorRef} disabled={disabled} style={triggerStyle} onPress={openDropdown} onHoverIn={() => setIsHovered(true)} onHoverOut={() => setIsHovered(false)} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} {...triggerKeyboardProps} accessibilityRole="button" accessibilityLabel={placeholder}>
          <TextInput type="text" value={currentHasValue ? displayText : ""} placeholder={placeholder} icon={icon} readonly error={error} slotRight={slotRight} isFocusedOverride={open || isFocused} isHoveredOverride={isHovered} reserveErrorSpace={reserveErrorSpace} testID={testID}/>
        </Pressable>
      </View>
      <ThemedDropdown open={open} anchorRef={anchorRef} onClose={() => setOpen(false)} testIDBackdrop="select-backdrop" yOffset={-30}>
        <View style={styles.listContainer}>
          <FlatList data={options} keyExtractor={(o, idx) => String(o.value ?? idx)} renderItem={renderOption} style={styles.listContainerFlatList} numColumns={numColumns}/>
          {dropdownFooter ? (<View style={styles.dropdownFooter}>{dropdownFooter}</View>) : null}
        </View>
      </ThemedDropdown>
    </View>);
}
const styles = StyleSheet.create({
    formGroup: {
        marginBottom: 0,
        position: "relative",
    },
    disabled: {
        opacity: 0.5,
    },
    clear: {
        paddingLeft: 6,
    },
    listContainer: {
        gap: 12,
    },
    listContainerFlatList: {
        maxHeight: 320,
    },
    option: {
        paddingHorizontal: 8,
        paddingVertical: 10,
        borderRadius: 4,
        gap: 8,
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
    },
    optionLabel: {
        fontSize: 16,
        flexGrow: 1,
    },
    dropdownFooter: {
        paddingTop: 8,
    },
    focusedBorder: {
        borderWidth: 2,
    },
});
//# sourceMappingURL=Select.js.map